import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addYears } from 'date-fns';
import Chart, { FontSpec } from 'chart.js/auto';
import { PortalRoutePath } from '@models';
import { CUSTOM_THEME, RxUtils, Tr, TypeUtils } from '@utils';
import { electrificationWizardService, notificationService, SimulationSavings } from '@services';
import { Alert, Stack } from '@mui/material';
import { DarkChartLegends, LightCardWithTitle } from '@components/common';
import { ElectrificationWizardConfirmationDialog } from '../ElectrificationWizardConfirmationDialog';

const getCommonFontConfig = (): Partial<FontSpec> => ({
	family: 'Montserrat',
	size: 16,
	lineHeight: 1.44
});

export interface IChartCO2ReductionProps {
	savings?: SimulationSavings;
}

export const ChartCO2Reduction: FC<IChartCO2ReductionProps> = ({ savings }) => {
	const { t } = useTranslation('portal');
	const navigate = useNavigate();
	const chartRef = useRef<HTMLCanvasElement>(null);
	const [chartInstance, setChartInstance] = useState<Chart>();
	const [selectedYear, setSelectedYear] = useState<number>();

	useEffect(() => {
		if (chartRef.current && savings) {
			chartInstance?.destroy();
			setChartInstance(
				new Chart(chartRef.current, {
					type: 'scatter',
					data: {
						datasets: [
							{
								type: 'bar',
								label: t('dashboard.executive-summary-savings.endothermic-vehicles-replaced-with-full-electric'),
								data: savings.yearlySavings.map(({ vehiclesElectrified }) => vehiclesElectrified?.length || 0),
								order: 1,
								backgroundColor: CUSTOM_THEME.palette.primary.main,
								borderRadius: 6,
								borderSkipped: false,
								maxBarThickness: 24
							},
							{
								type: 'line',
								yAxisID: 'yAxis2',
								label: t('dashboard.executive-summary-savings.co2-emission'),
								data: savings.yearlySavings.map(({ co2Total }) => co2Total / 1000000),
								pointRadius: 4,
								pointBorderColor: CUSTOM_THEME.palette.common.white,
								pointBorderWidth: 2,
								pointHoverRadius: 6,
								pointHoverBorderColor: CUSTOM_THEME.palette.common.white,
								pointHoverBorderWidth: 3,
								backgroundColor: CUSTOM_THEME.extraColors.pastelRed,
								borderColor: CUSTOM_THEME.extraColors.pastelRed
							}
						],
						labels: savings.yearlySavings.map(({ year }) => year)
					},
					options: {
						layout: {
							padding: { top: 32, bottom: 8 }
						},
						scales: {
							x: {
								type: 'category',
								ticks: { font: getCommonFontConfig() }
							},
							y: {
								beginAtZero: true,
								ticks: { font: getCommonFontConfig(), precision: 0 }
							},
							yAxis2: {
								axis: 'y',
								type: 'linear',
								position: 'right',
								grid: { display: false },
								min: 0,
								// max: 300,
								ticks: {
									font: getCommonFontConfig(),
									callback: (tickValue) => `${tickValue} ton`
								}
							}
						},
						plugins: {
							legend: { display: false },
							tooltip: {
								intersect: true,
								mode: 'nearest',
								position: 'nearest',
								backgroundColor: '#130c45',
								displayColors: false,
								caretSize: 8,
								caretPadding: 16,
								padding: {
									top: 8,
									bottom: 8,
									right: 24,
									left: 24
								},
								bodyFont: getCommonFontConfig(),
								callbacks: {
									label: ({ chart, dataIndex }) => {
										const year = chart.data.labels?.[dataIndex] as string;
										const vehicle = chart.data.datasets.find(
											(ds) =>
												ds.label ===
												t('dashboard.executive-summary-savings.endothermic-vehicles-replaced-with-full-electric')
										)?.data[dataIndex];
										const co2Reduction = chart.data.datasets.find(
											(ds) => ds.label === t('dashboard.executive-summary-savings.co2-emission')
										)?.data[dataIndex];

										return [
											`${t('dashboard.executive-summary-savings.year')}: ${year}`,
											`${t('dashboard.executive-summary-savings.co2-emission')}: ${
												typeof co2Reduction === 'number' ? co2Reduction.toFixed(2) : co2Reduction
											} ton`,
											`${t('dashboard.executive-summary-savings.vehicle')}: ${vehicle}`
										];
									}
								}
							}
						},
						onClick: (_e, elements) => {
							let year = new Date().getFullYear();
							if (TypeUtils.isNumber(elements?.[0]?.index)) {
								year = savings.yearlySavings[elements[0]?.index]?.year ?? year;
							}
							setSelectedYear(year);
						},
						onHover: (event, elements) => {
							const target = event?.native?.target as HTMLCanvasElement;
							target?.style && (target.style.cursor = elements[0] ? 'pointer' : 'default');
						}
					}
				})
			);
		}
	}, [chartRef, savings]);

	return (
		<>
			<LightCardWithTitle
				headerVariant="dark"
				title={
					<Tr.Portal
						path="dashboard.executive-summary-savings.co2-reduction-title"
						options={{ year: addYears(new Date(), savings?.yearlySavings.length || 0).getFullYear() }}
					/>
				}
				actionButton={
					<Alert
						variant="outlined"
						severity="error"
						icon={false}
						sx={{
							marginY: 0.75,
							borderRadius: 1.5,
							paddingX: 1,
							paddingY: 0.25,
							lineHeight: 'normal',
							border: 'solid 1px #f35d6a',
							backgroundColor: 'rgba(239, 149, 157, 0.27)',
							color: '#f35d6a'
						}}
					>
						<Tr.Portal
							path="dashboard.executive-summary-savings.reduced-emission-in-years"
							options={{ percent: -50, years: 2 }}
						/>
					</Alert>
				}
			>
				<Stack direction="column" alignItems="center" spacing={3}>
					<canvas ref={chartRef} />
					<DarkChartLegends
						sx={{ minWidth: 500 }}
						legends={[
							{
								color: CUSTOM_THEME.palette.primary.main,
								title: <Tr.Portal path="dashboard.executive-summary-savings.new-bev" />
							},
							{
								color: CUSTOM_THEME.extraColors.pastelRed,
								title: <Tr.Portal path="dashboard.executive-summary-savings.co2-emission" />
							}
						]}
					/>
				</Stack>
			</LightCardWithTitle>
			<ElectrificationWizardConfirmationDialog
				open={!!selectedYear}
				year={selectedYear}
				buttonProps={{ 'data-testid': 'goto-wizard-from-co2-reduction-accept-button' }}
				onClose={() => setSelectedYear(undefined)}
				onAccept={() => {
					const taskIds =
						savings?.yearlySavings?.find(({ year }) => year === selectedYear)?.vehiclesElectrified?.map(({ id }) => id) || [];
					if (taskIds.length) {
						RxUtils.promisify(electrificationWizardService.create(taskIds), () => {
							notificationService.sendSuccess(t('wizard.create-success'));
							navigate(`/portal/${PortalRoutePath.WIZARD}/processing`);
						});
					}
					setSelectedYear(undefined);
				}}
			/>
		</>
	);
};
